/**
 * Media Panel Component for Reels video editor
 * Import and manage media files (video, audio, images)
 */

import React, { useRef, useCallback, useState } from 'react';
import { Upload, Video, Music, Image, Trash2, Plus, Folder, Search, Grid, List, Clock } from 'lucide-react';
import { useProject, useUI } from '../stores/projectStore';
import {
  getMediaType,
  createMediaUrl,
  getVideoMetadata,
  getAudioMetadata,
  getImageMetadata,
  generateVideoThumbnail,
  formatFileSize,
  getAcceptString,
} from '../utils/mediaUtils';

const MediaPanel = () => {
  const { project, actions } = useProject();
  const { ui, actions: uiActions } = useUI();
  const fileInputRef = useRef(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [filter, setFilter] = useState('all'); // 'all' | 'video' | 'audio' | 'image'
  const [searchTerm, setSearchTerm] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Handle file import
  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsImporting(true);

    for (const file of files) {
      try {
        const type = getMediaType(file);
        if (type === 'unknown') continue;

        const url = createMediaUrl(file);
        let metadata = {};
        let thumbnail = null;

        if (type === 'video') {
          metadata = await getVideoMetadata(file);
          thumbnail = await generateVideoThumbnail(file, 0);
        } else if (type === 'audio') {
          metadata = await getAudioMetadata(file);
        } else if (type === 'image') {
          metadata = await getImageMetadata(file);
          thumbnail = url;
        }

        const mediaItem = {
          id: `media-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          type,
          file,
          url,
          thumbnail,
          size: file.size,
          duration: metadata.duration || (type === 'image' ? 5 : 0), // Default 5s for images
          width: metadata.width,
          height: metadata.height,
          addedAt: new Date().toISOString(),
        };

        actions.addMedia(mediaItem);
      } catch (error) {
        console.error('Failed to import media:', error);
      }
    }

    setIsImporting(false);
    e.target.value = '';
  }, [actions]);

  // Add media to timeline
  const handleAddToTimeline = useCallback((media) => {
    const trackType = media.type === 'audio' ? 'audio' : 'video';
    const track = project.tracks.find(t => t.type === trackType);

    if (!track) return;

    // Find the end time of existing clips on this track
    const endTime = track.clips.reduce((max, clip) => {
      return Math.max(max, clip.startTime + clip.duration);
    }, 0);

    const clip = {
      id: `clip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      mediaId: media.id,
      name: media.name,
      startTime: endTime,
      duration: media.duration,
      inPoint: 0,
      outPoint: media.duration,
      effects: [],
      volume: 1,
      speed: 1,
    };

    actions.addClip(track.id, clip);
  }, [project.tracks, actions]);

  // Delete media
  const handleDeleteMedia = useCallback((mediaId) => {
    actions.removeMedia(mediaId);
  }, [actions]);

  // Filter media
  const filteredMedia = project.mediaLibrary.filter(media => {
    if (filter !== 'all' && media.type !== filter) return false;
    if (searchTerm && !media.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Get icon for media type
  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'audio': return Music;
      case 'image': return Image;
      default: return Folder;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-white mb-1">Media Library</h3>
        <p className="text-xs text-slate-500">Import clips, images, and audio to start building.</p>

        {/* Import button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isImporting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white rounded-lg font-medium transition-colors"
        >
          {isImporting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Import Media
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={getAcceptString()}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Search and filters */}
      <div className="p-3 border-b border-slate-800 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search media..."
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
          />
        </div>

        <div className="flex items-center gap-1">
          {['all', 'video', 'audio', 'image'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${filter === f ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}

          <div className="flex-1" />

          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Media list */}
      <div className="flex-1 overflow-auto p-3">
        {filteredMedia.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Folder className="w-12 h-12 text-slate-700 mb-3" />
            <p className="text-slate-500 text-sm">No media files</p>
            <p className="text-slate-600 text-xs mt-1">Import videos, audio, or images to get started</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-2">
            {filteredMedia.map((media) => {
              const Icon = getMediaIcon(media.type);
              return (
                <div
                  key={media.id}
                  className="group relative bg-slate-800 rounded-lg overflow-hidden hover:ring-2 hover:ring-teal-500 transition-all cursor-pointer"
                  onClick={() => handleAddToTimeline(media)}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-slate-900 flex items-center justify-center">
                    {media.thumbnail ? (
                      <img src={media.thumbnail} alt={media.name} className="w-full h-full object-cover" />
                    ) : (
                      <Icon className="w-8 h-8 text-slate-600" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-2">
                    <p className="text-xs text-white truncate">{media.name}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                      {media.duration > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-3 h-3" />
                          {Math.floor(media.duration)}s
                        </span>
                      )}
                      <span>{formatFileSize(media.size)}</span>
                    </div>
                  </div>

                  {/* Add overlay */}
                  <div className="absolute inset-0 bg-teal-600/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Plus className="w-8 h-8 text-white" />
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMedia(media.id);
                    }}
                    className="absolute top-1 right-1 p-1 bg-slate-900/80 rounded opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all"
                  >
                    <Trash2 className="w-3 h-3 text-white" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredMedia.map((media) => {
              const Icon = getMediaIcon(media.type);
              return (
                <div
                  key={media.id}
                  className="group flex items-center gap-3 p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer"
                  onClick={() => handleAddToTimeline(media)}
                >
                  {/* Thumbnail */}
                  <div className="w-12 h-12 rounded bg-slate-900 flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {media.thumbnail ? (
                      <img src={media.thumbnail} alt={media.name} className="w-full h-full object-cover" />
                    ) : (
                      <Icon className="w-5 h-5 text-slate-600" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{media.name}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="capitalize">{media.type}</span>
                      {media.duration > 0 && <span>{Math.floor(media.duration)}s</span>}
                      <span>{formatFileSize(media.size)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToTimeline(media);
                      }}
                      className="p-1.5 bg-teal-600 rounded hover:bg-teal-500"
                    >
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMedia(media.id);
                      }}
                      className="p-1.5 bg-slate-600 rounded hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPanel;
